/**
 * Frontend-safe ESM inference helpers.
 * Keep this file free of CommonJS (`require`, `module.exports`) so production builds do not crash.
 */

const BUILDING_RE =
  /\b(cathedral|basilica|abbey|monastery|mosque|synagogue|palace|castle|fortress|tower|bridge|museum|gallery|library|temple|shrine|church|chapel|ruins?|memorial|monument|city\s+hall|town\s+hall|station|opera\s+house|courthouse)\b/i;

const PERSON_NAME_RE =
  /^(?:[A-Z][a-z.'-]+|Mc[A-Za-z][a-z.'-]+|Mac[A-Za-z][a-z.'-]+|O'[A-Za-z][a-z.'-]+)(?:\s+[A-Z][a-z.'-]+){1,4}$/;

const SEASON_OR_TIME_LED_RE =
  /^(winter|summer|spring|autumn|fall|morning|evening|midday|noon|night|midnight|dawn|daybreak)\b/i;

export function looksLikeNamedBuilding(raw = '', t = '') {
  const text = t || String(raw || '').toLowerCase();
  return BUILDING_RE.test(text);
}

export function looksLikePersonNameTitle(raw = '', t = '', manuscriptLike = false) {
  if (manuscriptLike) return false;
  if (looksLikeNamedBuilding(raw, t)) return false;
  const trimmed = String(raw || '').replace(/\s+/g, ' ').trim();
  if (!trimmed) return false;
  if (SEASON_OR_TIME_LED_RE.test(trimmed)) return false;
  if (/\b(still\s+life|landscape|seascape|view\s+of|market\s+scene|street\s+scene|interior|madonna|christ)\b/i.test(trimmed)) {
    return false;
  }
  return PERSON_NAME_RE.test(trimmed);
}

export function inferArtworkTypeFromTitle(title = '') {
  const raw = String(title || '').trim();
  const t = raw.toLowerCase();
  if (!t) return '';

  const manuscriptLike =
    /\b(prologue|preliminaries|epilogue|chapter|folio|manuscript|illuminated|miniature from|book of|gospel|lectionary)\b/i.test(t) ||
    /\b(apostles?\s+stating|apostol|apostoles|evangelists?|miniature\s+from)\b/i.test(t);

  if (/\bself-portrait\b/i.test(t) || /\bself portrait\b/i.test(t)) return 'Self-Portrait';
  if (/\bstill\s+life\b/i.test(t) || t.includes('nature morte')) return 'Still Life';
  if (!manuscriptLike && /\bportraits?\b/i.test(t)) return 'Portrait';
  if (looksLikeNamedBuilding(raw, t)) return 'Architectural';
  if (looksLikePersonNameTitle(raw, t, manuscriptLike)) return 'Portrait';

  if (/\b(flowers?|floral|bouquet|rose|tulip|lily|orchid|sunflower|chrysanthemum|garden)\b/i.test(t)) return 'Floral';
  if (/\b(horse|cow|deer|wolf|fox|bear|lion|tiger|elephant|dog|cat|bird|eagle|owl|parrot|sparrow|fish|whale|dolphin)\b/i.test(t)) {
    return 'Animal/Birds';
  }
  if (/\b(seascape|ship|harbor|harbour|coast|ocean|sea|beach|bay|waves?|boat|marina)\b/i.test(t)) return 'Seascape';
  if (/\b(madonna|christ|jesus|crucifix|nativity|annunciation|assumption|pieta|resurrection|holy\s+family|saint)\b/i.test(t)) return 'Sacred';
  if (/\b(tavern|wedding|market\s+scene|street\s+scene|crowd|picnic|banquet|kitchen\s+scene|daily\s+life|everyday\s+life|domestic\s+scene)\b/i.test(t)) {
    return 'Genre Scene';
  }
  if (/\b(sketches?|studies|study|étude|etude|cartoon\s+for|preparatory)\b/i.test(t)) return 'Study/Sketch';
  if (/\b(landscape|countryside|pastoral|meadow|field|forest|valley|mountain|hill|desert|river|lake|village|cityscape|sunset|sunrise|twilight|morning|evening|dawn)\b/i.test(t)) {
    return 'Landscape';
  }
  if (/\binterior\b/i.test(t)) return 'Interior';
  if (/\b(sculpture|statue|bronze|terracotta)\b/i.test(t)) return 'Sculpture';
  if (/\bcalligraphy\b/i.test(t)) return 'Calligraphy';
  if (/\bminiature\b/i.test(t) && !manuscriptLike) return 'Miniature';
  if (/\b(fantasy|myth|mythological|dragon|unicorn|fairy|nymph|satyr|centaur)\b/i.test(t)) return 'Fantasy';
  if (/\babstract\b/i.test(t)) return 'Abstract';
  if (/\b(objects?|objet|vanitas|trompe)\b/i.test(t)) return 'Objects';
  return '';
}

export function normalizeArtworkType(type = '') {
  const t = String(type || '').trim();
  if (!t) return '';
  if (t === 'Cityscape') return 'Architectural';
  if (t === 'Flower painting' || t === 'Flower Painting') return 'Floral';
  if (t === 'Animal' || t === 'Animals' || t === 'Animal / Birds') return 'Animal/Birds';
  if (t === 'Architectural/Building') return 'Architectural';
  return t;
}

export function resolveArtworkTypeForRow(row = {}) {
  const existingCandidates = [
    row.typeOfArtwork,
    row.artworkType,
    row.artType,
    row.originalTypeOfArtwork,
    row.originalArtworkType,
    row.sourceTypeOfArtwork,
    row.libraryTypeOfArtwork,
  ];
  for (const c of existingCandidates) {
    const n = normalizeArtworkType(c);
    if (n) return { type: n, source: 'existing' };
  }
  const inferred = inferArtworkTypeFromTitle(row.title || row.artworkTitle || row.verifiedTitle || '');
  return inferred ? { type: inferred, source: 'name_inferred' } : { type: '', source: 'missing' };
}

export default {
  inferArtworkTypeFromTitle,
  looksLikeNamedBuilding,
  looksLikePersonNameTitle,
  normalizeArtworkType,
  resolveArtworkTypeForRow,
};
